import { NodeId } from 'rete';
import { ArrangeAppliers, ExpectedSchemes } from 'rete-auto-arrange-plugin';

/**
 * Auto-arrange applier that only moves nodes and never resizes them.
 *
 * The plugin's `StandardApplier` calls `area.resize(id, width, height)` for every laid-out node, and
 * `NodeView.resize` writes inline `style.width` / `style.height` onto the node element. That permanently
 * deforms nodes whose size is owned by their renderer (circular terminal nodes) or by the user
 * (resizable annotations). Overriding `resizeNode` keeps the rest of `StandardApplier` intact.
 */
export class PositionOnlyApplier<Schemes extends ExpectedSchemes, Extra> extends ArrangeAppliers.StandardApplier<
  Schemes,
  Extra
> {
  protected override async resizeNode(): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Snaps the layer position only, never the position within a layer.
   *
   * elk returns fractional coordinates, so the corridor between two layers ends up sized by accumulated
   * float drift rather than by the spacing asked for: two corridors measured 0.2px apart in width offered
   * four and three usable routing columns. Snapping x to the grid the canvas and the A* router already
   * use makes every corridor a whole number of cells.
   *
   * y is deliberately left alone. Node pitch within a layer is height plus spacing, which is not a grid
   * multiple, so rounding each node independently pushes consecutive nodes in opposite directions — a
   * uniform 40px gap was measured degrading to an alternating 26/46. Nothing routes between siblings in
   * a layer, so their exact offset does not need to be grid-aligned, while their even spacing does.
   */
  protected override async translateNode(id: NodeId, x: number, y: number): Promise<void | boolean> {
    return super.translateNode(id, snapToCanvasGrid(x), y);
  }
}

/** Matches `AreaExtensions.snapGrid` and the A* router's cell size, both 20px. */
const CANVAS_GRID_SIZE = 20;

function snapToCanvasGrid(value: number): number {
  return Math.round(value / CANVAS_GRID_SIZE) * CANVAS_GRID_SIZE;
}

/* eslint-disable @typescript-eslint/naming-convention -- elk option keys are dotted literals */
/**
 * elk options used when arranging a flow graph. The plugin preset already supplies
 * `elk.algorithm`, `elk.hierarchyHandling` and `elk.edgeRouting`; these are merged on top of it.
 *
 * Spacing is deliberately above elk's 20px default, and the reason is the connection router rather
 * than taste. Paths are found by A* over a grid of `FlowService.#cellSize` (20px) cells, so elk's
 * default gap leaves a single cell for a path to thread between two nodes. These values give two
 * cells between stacked nodes and four between layers, where connections fan out.
 *
 * Measured against the alternatives, this costs roughly a quarter more edge length and a third more
 * area than elk's defaults, which is the price of routable corridors. `elk.direction: RIGHT` and
 * `elk.separateConnectedComponents` were both measured to be exact no-ops here — elk's layered
 * algorithm already does both — so they are deliberately not restated.
 *
 * Known limitation, measured rather than assumed: two disconnected flows end up stacked the same 40px
 * apart as two siblings within one flow, so they do not read as separate. `elk.spacing.componentComponent`
 * cannot fix it — the preset's `elk.hierarchyHandling: INCLUDE_CHILDREN` makes elk ignore that option
 * entirely (verified at 20, 160 and 400, all giving an identical 40px). Switching to `SEPARATE_CHILDREN`
 * does restore it but then overlaps the two components on both axes, and this editor does use rete's
 * `parent` (see `components/nodes/utils/node.helper.ts`), so that switch is not free either.
 */
export const flowArrangeLayoutOptions: Record<string, string> = {
  'elk.spacing.nodeNode': '40',
  'elk.layered.spacing.nodeNodeBetweenLayers': '80',
};
/* eslint-enable @typescript-eslint/naming-convention */
